/**
 * Brew University: the founders' product walkthroughs on YouTube,
 * grouped the way someone learns Brew. Chapters and
 * their start times come from the transcribed video library (each `t` sits
 * a few seconds before its topic, as the library sets it); upload dates and
 * lengths are YouTube's. `/university`, its Markdown twin and the videos
 * shown beside related pages all read this file.
 */

export const UNIVERSITY_PATH = '/university'

export const UNIVERSITY_CHANNEL_URL =
  'https://www.youtube.com/@brewdotnew/videos'

export const UNIVERSITY_PAGE = {
  eyebrow: 'Brew University',
  heading: 'Learn Brew in an afternoon',
  description:
    'Short walkthroughs from Philip and Thomas, Brew’s founders: set up your brand, design and send emails, build audiences and automations, and run it all from Claude.',
} as const

type UniversityChapter = Readonly<{ title: string; t: number }>

export type UniversityVideo = Readonly<{
  /** The YouTube video ID. */
  id: string
  title: string
  description: string
  /** Who walks through it, as the video and its thumbnail name them. */
  presenter: 'Philip' | 'Thomas'
  seconds: number
  uploadDate: string
  chapters: ReadonlyArray<UniversityChapter>
}>

export const UNIVERSITY_VIDEOS = {
  createEditSend: {
    id: 'eZyx59lzEz8',
    title: 'How to Create, Edit, and Send Emails in Brew',
    description:
      'Generate an email from a prompt, edit it by hand or with AI, send it to an audience and check opens and clicks.',
    presenter: 'Philip',
    seconds: 374,
    uploadDate: '2026-08-03T13:38:44-07:00',
    chapters: [
      { title: 'Generate an email from a prompt', t: 12 },
      { title: 'Manual or AI-assisted editing', t: 87 },
      { title: 'Replace an image with a GIF', t: 115 },
      { title: 'Choose an audience and sending domain', t: 175 },
      { title: 'Send now, schedule, intelligent or gradual send', t: 250 },
      { title: 'Opens and clicks in Analytics', t: 324 },
    ],
  },
  brand: {
    id: 'PPGG6XkfcvE',
    title: 'How to Extract Your Brand and Create On-Brand Emails with Brew',
    description:
      'Paste your website and Brew collects your logos, colors, fonts and images and writes the email design MD every email follows.',
    presenter: 'Thomas',
    seconds: 266,
    uploadDate: '2026-08-03T13:39:47-07:00',
    chapters: [
      { title: 'Your website and files', t: 6 },
      { title: 'Sending from your own domain', t: 36 },
      { title: 'The email design MD', t: 85 },
      { title: 'Images, GIFs and links', t: 166 },
      { title: 'Editing your design system', t: 198 },
      { title: 'Your unsubscribe page', t: 238 },
    ],
  },
  consistentDesign: {
    id: '8bO76hfPuog',
    title: 'How to Create Consistent On-Brand Email Designs with Brew',
    description:
      'Ask for alternatives, refine the one you like, then save it as a golden reference so every future email follows it.',
    presenter: 'Philip',
    seconds: 506,
    uploadDate: '2026-08-03T13:38:56-07:00',
    chapters: [
      { title: 'Prompting for alternative styles', t: 36 },
      { title: 'Refining in chat or by hand', t: 136 },
      { title: 'Updating the email design MD', t: 192 },
      { title: 'Saving a golden reference', t: 249 },
      { title: 'Starting from a template', t: 301 },
      { title: 'Remixing an email into your brand', t: 337 },
      { title: 'Fixing logos and assets', t: 426 },
    ],
  },
  importEmails: {
    id: 'm1QowAm1IDA',
    title: 'How to Import Figma, HTML, and Inbox Emails in Brew',
    description:
      'Bring the emails you already have into Brew from Figma, from HTML or by forwarding them from your inbox.',
    presenter: 'Thomas',
    seconds: 215,
    uploadDate: '2026-08-03T13:38:47-07:00',
    chapters: [
      { title: 'Import a Figma design', t: 12 },
      { title: 'Convert an HTML email', t: 51 },
      { title: 'Forward an email from your inbox', t: 92 },
      { title: 'Edit or remix the import', t: 165 },
    ],
  },
  rendering: {
    id: 'JZEyZaZ6aCg',
    title: 'How to Check Email Rendering Before You Send with Brew',
    description:
      'Check your domain’s health, preview the email in Gmail, Outlook and Apple Mail, and test which inbox it lands in.',
    presenter: 'Philip',
    seconds: 272,
    uploadDate: '2026-08-03T13:39:11-07:00',
    chapters: [
      { title: 'Why check before a big send', t: 7 },
      { title: 'Domain health and deliverability', t: 102 },
      { title: 'Render previews across inboxes', t: 158 },
      { title: 'Inbox placement results', t: 191 },
      { title: 'What to fix next', t: 219 },
    ],
  },
  contacts: {
    id: 'HxfYKJbE4WM',
    title: 'How to Import Contacts and Build Targeted Audiences with Brew',
    description:
      'Import a CSV, validate your contacts, then build audiences with filters or by asking in plain language.',
    presenter: 'Thomas',
    seconds: 300,
    uploadDate: '2026-08-03T13:39:08-07:00',
    chapters: [
      { title: 'Import a CSV', t: 19 },
      { title: 'Map columns and confirm subscriptions', t: 45 },
      { title: 'Validate contacts', t: 82 },
      { title: 'Filter and create an audience', t: 174 },
      { title: 'Build audiences with AI search', t: 218 },
    ],
  },
  automations: {
    id: 'F2ReiamTeM8',
    title: 'How to Set Up Powerful Email Automations in Brew',
    description:
      'Start a flow from Stripe, Clerk, Shopify or your own events, and shape it with waits, filters and splits.',
    presenter: 'Philip',
    seconds: 440,
    uploadDate: '2026-08-03T13:38:50-07:00',
    chapters: [
      { title: 'Choose a trigger', t: 55 },
      { title: 'Build a failed-payment flow', t: 108 },
      { title: 'Use Stripe data in the email', t: 158 },
      { title: 'Brew’s own welcome flow', t: 252 },
      { title: 'Waits, filters and splits', t: 287 },
      { title: 'Pause or edit a live flow', t: 359 },
    ],
  },
  claude: {
    id: 'W8ZATx3v_YY',
    title: 'How to Connect Claude to Brew with MCP',
    description:
      'Add Brew to Claude as a custom connector, then create an on-brand email, find the right audience and schedule the send from Claude.',
    presenter: 'Thomas',
    seconds: 461,
    uploadDate: '2026-08-03T13:38:53-07:00',
    chapters: [
      { title: 'Add Brew as a custom connector', t: 37 },
      { title: 'Choose your brand', t: 63 },
      { title: 'Create an email from Claude', t: 133 },
      { title: 'Bring in your CRM and other tools', t: 237 },
      { title: 'Find recipients and schedule the send', t: 270 },
    ],
  },
  grokBot: {
    id: 'cQRBsrwNKyA',
    title: 'Turn a LinkedIn Post into an On-Brand Email with Grok Bot',
    description:
      'A one-minute example: Grok Bot turns a LinkedIn post into an on-brand email through Brew.',
    presenter: 'Philip',
    seconds: 58,
    uploadDate: '2026-08-16T04:14:24-07:00',
    chapters: [],
  },
} as const satisfies Record<string, UniversityVideo>

export type UniversityVideoKey = keyof typeof UNIVERSITY_VIDEOS

/** The page's sections, in the order someone learns Brew. */
export const UNIVERSITY_GROUPS: ReadonlyArray<
  Readonly<{
    id: string
    title: string
    description: string
    videos: ReadonlyArray<UniversityVideoKey>
  }>
> = [
  {
    id: 'start',
    title: 'Get started',
    description: 'Set up your brand and send your first email.',
    videos: ['brand', 'createEditSend'],
  },
  {
    id: 'design',
    title: 'Design emails',
    description:
      'Keep every email on-brand, bring in the ones you have, and check them before they go out.',
    videos: ['consistentDesign', 'importEmails', 'rendering'],
  },
  {
    id: 'audiences',
    title: 'Audiences and automations',
    description:
      'Bring in your contacts and let your product’s events send the right email.',
    videos: ['contacts', 'automations'],
  },
  {
    id: 'agents',
    title: 'Brew from your AI assistant',
    description: 'Run Brew from Claude, Grok and other MCP clients.',
    videos: ['claude', 'grokBot'],
  },
]

/** "7:41". */
export function formatVideoLength(seconds: number): string {
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`
}

/** "PT7M41S", for VideoObject structured data. */
export function isoVideoDuration(seconds: number): string {
  return `PT${Math.floor(seconds / 60)}M${seconds % 60}S`
}

/** A link that opens the video at `t` seconds on YouTube. */
export function videoUrlAt(video: UniversityVideo, t = 0): string {
  return t > 0
    ? `https://youtu.be/${video.id}?t=${t}`
    : `https://youtu.be/${video.id}`
}
